import { useMemo } from 'react'

import { isTruthy } from '@bemlo/utils'

import { useTranslations } from '../../../../translate'
import { Cell } from '../components/Cell'
import { CellShiftsColumn } from '../components/CellShiftsColumn'
import { NewShift } from '../components/NewShift'
import { ResourceTitle } from '../components/ResourceTitle'
import { Row } from '../components/Row'
import { useSchedule } from '../useSchedule'
import { getShiftsInWeek } from '../utils/get-shifts-in-week'

type ExternalResourcesProps = {
  homeUnitId: string
  scheduleResourceIds: string[]
}

export const ExternalResources = (props: ExternalResourcesProps) => {
  const t = useTranslations()
  const {
    days,
    currentWeek,
    shifts: allShifts,
    shiftPopover,
    resourcesIdMap,
    resourcesRowKeyMap,
  } = useSchedule()

  const [shifts, shiftsInWeek] = useMemo(() => {
    const shiftsWithBookings = allShifts
      .map((shift) => {
        if (shift.bookedCandidateId) {
          return {
            ...shift,
            bookedCandidate: resourcesIdMap.get(shift.bookedCandidateId),
          }
        }

        if (typeof shift.rowKey === 'number') {
          return {
            ...shift,
            bookedCandidate: resourcesRowKeyMap.get(shift.rowKey),
          }
        }
      })
      .filter(isTruthy)

    const shifts = shiftsWithBookings.filter((shift) => {
      if (!props.scheduleResourceIds.includes(shift.scheduleResourceId)) {
        return false
      }

      return shift.bookedCandidate
        ? shift.bookedCandidate.homeUnitId !== props.homeUnitId
        : false
    })
    const shiftsInWeek = currentWeek
      ? getShiftsInWeek(currentWeek, shifts)
      : shifts

    return [shifts, shiftsInWeek]
  }, [allShifts, props, currentWeek])

  return (
    <Row>
      <Cell isTitle rowSpan={1}>
        <ResourceTitle
          title={t.shifts.external_resource_other()}
          shifts={shiftsInWeek}
          bold={false}
        />
      </Cell>
      {days.map((day) => {
        const shiftsOnDay = shifts.filter((shift) => shift.date === day.dateISO)

        return (
          <Cell
            key={day.dateISO}
            disabled={!day.withinRange}
            weekend={day.isWeekend}
          >
            <CellShiftsColumn>
              {shiftsOnDay.map((shift) => {
                const isArchived = shift.status === 'ARCHIVED'
                const isUrgent = shift.isUrgent && !isArchived

                if (!shift) return null

                const resource = shift.bookedCandidate
                  ? shift.bookedCandidate.fullName
                  : shift.rowKey !== undefined
                    ? t.shifts.row_n(shift.rowKey + 1)
                    : t.shifts.vacant_one()

                return (
                  <NewShift
                    key={shift.id}
                    shift={shift}
                    interestedCandidates={shift.interestedCandidates}
                    unitName={`${resource} - ${shift.scheduleResourceName}`}
                    popoverContent={shiftPopover}
                    shiftIds={[shift.id]}
                    status={shift.status}
                    isUrgent={isUrgent}
                    unselectable={isArchived}
                  />
                )
              })}
            </CellShiftsColumn>
          </Cell>
        )
      })}
    </Row>
  )
}
